import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { noop } from 'lodash'

import rem from '~/utils/ui'

const buttonStyles: SxProps<Theme> = {
  height: 'initial',
  width: 'initial',
  minWidth: 'initial',
  minHeight: 'initial',
  borderRadius: rem(8),
  letterSpacing: 0,
  fontWeight: 500,
  fontSize: rem(14),
  lineHeight: rem(20),
  '&&&': {
    paddingInline: rem(16),
    paddingBlock: rem(8),
  },
}
type TProps = {
  isOpen: boolean
  onClose: () => void
  onCancel: () => void
  title: string
  children?: React.ReactNode
  isDisabled?: boolean
  onConfirm?: (e: React.MouseEvent<HTMLElement>) => void
  cancelText?: string
  confirmText?: string
  sx?: SxProps<Theme>
  maxWidth?: number
  description?: React.ReactNode
  hideActions?: boolean
  shouldKeepCancelEnabled?: boolean
  confirmTooltip?: React.ReactNode
  confirmButtonTestId?: string
  cancelButtonTestId?: string
  // 'body' lets a tall dialog grow past the viewport and scroll the overlay
  // instead of the dialog content.
  scroll?: DialogProps['scroll']
  contentGap?: number
}

export default function CustomDialog({
  isOpen,
  onClose,
  onCancel,
  title,
  children = null,
  isDisabled = false,
  onConfirm = noop,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  sx = {},
  maxWidth = 500,
  description = null,
  hideActions = false,
  shouldKeepCancelEnabled = false,
  confirmTooltip = '',
  confirmButtonTestId = '',
  cancelButtonTestId = '',
  scroll = 'paper',
  contentGap = 32,
}: TProps) {
  const theme = useTheme()
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      scroll={scroll}
      sx={{
        '> .MuiDialog-container > .MuiDialog-paper': {
          padding: rem(24),
          '&.MuiPaper-root': {
            gap: rem(16),
            borderRadius: rem(8),
            maxWidth: rem(maxWidth),
            minWidth: rem(400),
          },
        },
        ...sx,
      }}
    >
      <DialogTitle sx={{ padding: 0, fontSize: rem(18), fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <IconButton
        disableRipple
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: rem(16),
          top: rem(16),
          color: theme.palette.grey[500],
          width: rem(16),
          height: rem(16),
          padding: 0,
        }}
      >
        <Close sx={{ fontSize: rem(16) }} />
      </IconButton>

      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(contentGap),
            overflow: 'hidden',
          }}
        >
          {description && typeof description === 'string' ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: rem(14),
                color: theme.palette.text.secondary,
                fontWeight: 400,
                whiteSpace: 'pre-line',
              }}
            >
              {description}
            </Typography>
          ) : (
            description
          )}
          {children}
        </Box>
      </DialogContent>

      {!hideActions && (
        <DialogActions sx={{ padding: 0, marginTop: rem(16) }}>
          <Button
            data-testid={cancelButtonTestId || undefined}
            disabled={!shouldKeepCancelEnabled && isDisabled}
            onClick={onCancel}
            sx={buttonStyles}
            variant="secondary"
          >
            {cancelText}
          </Button>
          <Tooltip
            title={confirmTooltip}
            placement="bottom"
            leaveDelay={400}
            disableInteractive={false}
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button
                data-testid={confirmButtonTestId || undefined}
                disabled={isDisabled}
                onClick={onConfirm}
                sx={buttonStyles}
                variant="primary"
              >
                {confirmText}
              </Button>
            </Box>
          </Tooltip>
        </DialogActions>
      )}
    </Dialog>
  )
}
